// SETTINGS V2 — Invite permanent employee dialog (SETTINGS_V2_SP6 §8.1).
//
// SettingsV2DialogShell consumer (600px desktop / fullscreen phone; Escape /
// backdrop / × / Cancel / hardware-Back close — all shell-provided). Open-state
// lives in settingsV2UiSlice.openDialog === "invitePermanent". Controlled state
// (NOT react-hook-form — SP2 ruling; parity is behaviour: payload, validators,
// disable logic). Binds the reused customerUsersSlice UNCHANGED. Roles fetched
// on open (cached per session — parity), silent failure → no select. Payload =
// { userName, firstName, lastName, department, roleId? }; submit is fulfilled-
// gated → close + getUsers. Inline styles + scoped CSS-var tokens + v2 atoms.
// No MUI/sx/dt/Tailwind. SEC-REQ-2: fetch only in the open effect.
import React, { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { emailRegex } from "../../../../shared/validators";
import { InviteUser } from "../../models/userModels";
import { inviteUser, getUsers, getRoles } from "../../customerUsersSlice";
import { useRoleLabel } from "../../utils/useRoleLabel";
import { closeDialog } from "../settingsV2UiSlice";
import { SInputField, SSelectField } from "../atoms";
import SettingsV2DialogShell from "./SettingsV2DialogShell";

type PermForm = {
  userName: string;
  firstName: string;
  lastName: string;
  department: string;
  roleId: string;
};

const EMPTY: PermForm = {
  userName: "",
  firstName: "",
  lastName: "",
  department: "",
  roleId: "",
};

const InvitePermanentDialogV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { translate: tAccount } = useTranslation("account");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone } = useBreakpoint();
  const getRoleLabel = useRoleLabel();

  const open = useAppSelector((s) => s.settingsV2Ui.openDialog === "invitePermanent");
  const loading = useAppSelector((s) => s.customerUsers.loading);
  const roles = useAppSelector((s) => s.customerUsers.roles);

  // Permanent invites must NOT offer the Visitor role (Viewer = QR-kiosk-only,
  // no web access — a permanent employee is never a Visitor). Discriminate on the
  // backend role CODE `role.name` ("Viewer"), NOT the localized label: `name` is
  // the stable, language-independent key the roles payload carries and that
  // getRoleLabel itself resolves via `roles.${role.name}` (see common.json roles:
  // SystemAdministrator/Admin/Manager/Worker/Viewer/CustomerUser). Temporary-invite
  // dialog behaviour is untouched.
  const assignableRoles = roles.filter((r) => r.name !== "Viewer");

  const [form, setForm] = useState<PermForm>(EMPTY);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const setField = (k: keyof PermForm) => (v: string) =>
    setForm((f) => ({ ...f, [k]: v }));
  const markTouched = (k: string) => setTouched((p) => ({ ...p, [k]: true }));

  // Roles on open — parity (legacy lines 60-64): fetch only when not cached.
  useEffect(() => {
    if (open && roles.length === 0) dispatch(getRoles());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Seed/reset on open (legacy reset() parity).
  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setTouched({});
    }
  }, [open]);

  const handleClose = () => {
    dispatch(closeDialog());
    setForm(EMPTY);
    setTouched({});
  };

  const emailValid = emailRegex.test(form.userName);
  const isFormValid = [form.userName, form.firstName, form.lastName].every((f) =>
    f.trim()
  );

  const userNameError = touched.userName
    ? form.userName.trim() === ""
      ? (tCommon("forms.required") as string)
      : !emailValid
      ? (tAccount("invalidEmail") as string)
      : undefined
    : undefined;
  const firstNameError =
    touched.firstName && form.firstName.trim() === ""
      ? (tCommon("forms.required") as string)
      : undefined;
  const lastNameError =
    touched.lastName && form.lastName.trim() === ""
      ? (tCommon("forms.required") as string)
      : undefined;
  const departmentError =
    touched.department && form.department.length > 20
      ? (tCommon("forms.maxLength", { max: 20 }) as string)
      : undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ userName: true, firstName: true, lastName: true, department: true });
    if (!isFormValid || !emailValid || form.department.length > 20 || loading) return;

    const model: InviteUser = {
      userName: form.userName,
      firstName: form.firstName,
      lastName: form.lastName,
      department: form.department, // send as typed (parity — omit-if-empty not required)
    };
    if (form.roleId) model.roleId = form.roleId; // omit when unset

    const result = await dispatch(inviteUser(model));
    if (inviteUser.fulfilled.match(result)) {
      dispatch(closeDialog());
      setForm(EMPTY);
      setTouched({});
      dispatch(getUsers());
    }
  };

  const footer = (
    <>
      <button type="button" className="btn btn-ghost" onClick={handleClose}>
        {tCommon("buttons.cancel") as string}
      </button>
      <button
        type="submit"
        form="invitePermFormV2"
        className="btn btn-primary"
        disabled={!isFormValid || loading}
        style={{ background: "var(--mod-accent)", borderColor: "var(--mod-accent)" }}
      >
        {(loading ? t("settings:sending") : t("settings:sendInvite")) as string}
      </button>
    </>
  );

  const twoCol: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
    gap: 12,
  };

  return (
    <SettingsV2DialogShell
      open={open}
      onClose={handleClose}
      width={600}
      title={t("settings:invitePermanentEmployee") as string}
      sub={t("settings:team.invitePermSub") as string}
      footerNote={t("settings:team.invitePermFooter") as string}
      labelledById="sv2-invite-perm-title"
      busy={loading}
      footer={footer}
    >
      <form
        id="invitePermFormV2"
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div onBlur={() => markTouched("userName")}>
          <SInputField
            label={tAccount("email") as string}
            required
            mono
            value={form.userName}
            onChange={setField("userName")}
            placeholder={tAccount("emailPlaceholder") as string}
            error={userNameError}
          />
        </div>
        <div style={twoCol}>
          <div onBlur={() => markTouched("firstName")}>
            <SInputField
              label={tAccount("firstName") as string}
              required
              value={form.firstName}
              onChange={setField("firstName")}
              placeholder={tAccount("firstNamePlaceholder") as string}
              error={firstNameError}
            />
          </div>
          <div onBlur={() => markTouched("lastName")}>
            <SInputField
              label={tAccount("lastName") as string}
              required
              value={form.lastName}
              onChange={setField("lastName")}
              placeholder={tAccount("lastNamePlaceholder") as string}
              error={lastNameError}
            />
          </div>
        </div>
        <div style={twoCol}>
          <div onBlur={() => markTouched("department")}>
            <SInputField
              label={tAccount("department") as string}
              maxLength={20}
              value={form.department}
              onChange={setField("department")}
              placeholder={tAccount("departmentPlaceholder") as string}
              error={departmentError}
            />
          </div>
          {assignableRoles.length > 0 && (
            <SSelectField
              label={t("settings:role") as string}
              value={form.roleId}
              onChange={(v) => setField("roleId")(String(v))}
              options={assignableRoles.map((r) => ({ value: r.id, label: getRoleLabel(r) }))}
              placeholder={t("settings:selectRole") as string}
            />
          )}
        </div>
      </form>
    </SettingsV2DialogShell>
  );
};

export default InvitePermanentDialogV2;
